import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2 } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

export default function AdminCMS() {
  const [tab, setTab] = useState("about");
  return (
    <div>
      <p className="text-[10px] tracking-luxury uppercase text-gold mb-4">Content</p>
      <h1 className="font-editorial text-5xl mb-8 tracking-tight">CMS</h1>
      <div className="flex gap-4 border-b border-ivory/15 mb-8">
        {["about","faqs","jobs","journal","chapters"].map(t => (
          <button key={t} data-testid={`cms-tab-${t}`} onClick={()=>setTab(t)}
            className={`pb-3 text-[11px] tracking-widest-plus uppercase ${tab===t?"text-gold border-b-2 border-gold":"text-ivory/60 hover:text-ivory"}`}>{t}</button>
        ))}
      </div>
      {tab === "about" && <AboutEditor />}
      {tab === "faqs" && <FAQsEditor />}
      {tab === "jobs" && <JobsEditor />}
      {tab === "journal" && <JournalEditor />}
      {tab === "chapters" && <ChapterFacesEditor />}
    </div>
  );
}

function AboutEditor() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/company").then(r => setC(r.data)); }, []);
  if (!c) return <p className="text-ivory/50">Loading…</p>;
  const update = (path, v) => setC(prev => {
    const next = JSON.parse(JSON.stringify(prev));
    const [a,b] = path.split(".");
    if (b) next[a][b] = v; else next[a] = v;
    return next;
  });
  const save = async () => { try { await api.patch("/admin/company", c); toast.success("Saved"); } catch { toast.error("Failed"); } };
  const abt = c.about || {};
  return (
    <div className="space-y-6 max-w-3xl">
      <p className="text-[11px] tracking-widest-plus uppercase text-gold">About copy</p>
      {["opening","how_started","vision","mission","philosophy","london_wear","founder_message"].map(k => (
        <label key={k} className="block">
          <span className="text-[10px] tracking-widest-plus uppercase text-ivory/50 capitalize">{k.replace(/_/g," ")}</span>
          <textarea rows={3} data-testid={`about-${k}`} value={abt[k] || ""} onChange={(e)=>update(`about.${k}`, e.target.value)}
            className="w-full bg-onyx border border-ivory/15 focus:border-gold p-3 outline-none text-ivory" />
        </label>
      ))}
      <button onClick={save} className="bg-gold text-ink px-6 py-3 text-[11px] tracking-widest-plus uppercase">Save changes</button>
    </div>
  );
}

function FAQsEditor() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ section: "General", question: "", answer: "" });
  const load = useCallback(() => api.get("/admin/faqs").then(r => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!f.question || !f.answer) return; await api.post("/admin/faqs", f); setF({section:"General",question:"",answer:""}); load(); };
  const del = async (id) => { await api.delete(`/admin/faqs/${id}`); load(); };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-3 items-end p-4" style={{background:"#141414"}}>
        <input placeholder="Section" data-testid="faq-section" value={f.section} onChange={e=>setF({...f,section:e.target.value})} className="bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <input placeholder="Question" data-testid="faq-question" value={f.question} onChange={e=>setF({...f,question:e.target.value})} className="col-span-3 bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <textarea placeholder="Answer" data-testid="faq-answer" value={f.answer} onChange={e=>setF({...f,answer:e.target.value})} className="col-span-4 bg-onyx border border-ivory/15 p-2 text-sm text-ivory" rows="2" />
        <button data-testid="faq-add" onClick={add} className="col-span-4 bg-gold text-ink py-2 text-[11px] tracking-widest-plus uppercase flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add FAQ</button>
      </div>
      <div className="divide-y divide-ivory/10 border border-ivory/10">
        {items.map(x => (
          <div key={x.id} className="p-4 flex justify-between gap-4 items-start">
            <div className="flex-1"><p className="text-[10px] tracking-widest-plus uppercase text-gold">{x.section}</p><p className="font-editorial text-lg">{x.question}</p><p className="text-sm text-ivory/60 mt-1">{x.answer}</p></div>
            <button onClick={()=>del(x.id)} className="text-ivory/50 hover:text-gold"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function JobsEditor() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title:"", department:"Design", location:"Lucknow", employment_type:"Full-time", deadline:"", description:"" });
  const load = useCallback(() => api.get("/admin/jobs").then(r => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!f.title) return; await api.post("/admin/jobs", f); setF({title:"",department:"Design",location:"Lucknow",employment_type:"Full-time",deadline:"",description:""}); load(); };
  const del = async (id) => { await api.delete(`/admin/jobs/${id}`); load(); };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-3 p-4" style={{background:"#141414"}}>
        <input placeholder="Job title" data-testid="job-title" value={f.title} onChange={e=>setF({...f,title:e.target.value})} className="col-span-2 bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <input placeholder="Department" value={f.department} onChange={e=>setF({...f,department:e.target.value})} className="bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <input placeholder="Location" value={f.location} onChange={e=>setF({...f,location:e.target.value})} className="bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <input placeholder="Employment type" value={f.employment_type} onChange={e=>setF({...f,employment_type:e.target.value})} className="bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <input placeholder="Deadline (YYYY-MM-DD)" value={f.deadline} onChange={e=>setF({...f,deadline:e.target.value})} className="bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <textarea placeholder="Description" value={f.description} onChange={e=>setF({...f,description:e.target.value})} className="col-span-4 bg-onyx border border-ivory/15 p-2 text-sm text-ivory" rows="2" />
        <button data-testid="job-add" onClick={add} className="col-span-4 bg-gold text-ink py-2 text-[11px] tracking-widest-plus uppercase flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add position</button>
      </div>
      <div className="divide-y divide-ivory/10 border border-ivory/10">
        {items.map(x => (
          <div key={x.id} className="p-4 flex justify-between gap-4">
            <div className="flex-1"><p className="text-[10px] tracking-widest-plus uppercase text-gold">{x.department}</p><p className="font-editorial text-lg">{x.title}</p><p className="text-sm text-ivory/60">{x.location} · {x.employment_type} · {x.deadline}</p></div>
            <button onClick={()=>del(x.id)} className="text-ivory/50 hover:text-gold"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function JournalEditor() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ slug:"", title:"", category:"Essay", excerpt:"", hero_image:"", body:"" });
  const [genBusy, setGenBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/admin/journal").then(r => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const reset = () => setF({slug:"",title:"",category:"Essay",excerpt:"",hero_image:"",body:""});
  const add = async () => {
    if (!f.title || !f.slug) { toast.error("Slug and title are required"); return; }
    setSaving(true);
    try { await api.post("/admin/journal", f); toast.success("Essay published"); reset(); load(); }
    catch { toast.error("Publish failed"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/admin/journal/${id}`); load(); };
  const generateCover = async () => {
    if (!f.title) { toast.error("Add a title first — the AI needs it"); return; }
    setGenBusy(true);
    try {
      const r = await api.post("/admin/journal/generate-cover", { title: f.title, subtitle: f.excerpt, category: f.category });
      setF(prev => ({ ...prev, hero_image: r.data.url }));
      toast.success("Cover generated");
    } catch (e) {
      toast.error("Cover generation failed — check LLM balance");
    } finally { setGenBusy(false); }
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3 p-4" style={{background:"#141414"}}>
        <input placeholder="Slug" data-testid="journal-slug" value={f.slug} onChange={e=>setF({...f,slug:e.target.value})} className="bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <input placeholder="Title" data-testid="journal-title" value={f.title} onChange={e=>setF({...f,title:e.target.value})} className="col-span-2 bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <input placeholder="Category" value={f.category} onChange={e=>setF({...f,category:e.target.value})} className="bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <input placeholder="Hero image URL (or generate below)" value={f.hero_image} onChange={e=>setF({...f,hero_image:e.target.value})} className="col-span-2 bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />
        <input placeholder="Excerpt / subtitle" value={f.excerpt} onChange={e=>setF({...f,excerpt:e.target.value})} className="col-span-3 bg-onyx border border-ivory/15 p-2 text-sm text-ivory" />

        {/* AI cover generator */}
        <div className="col-span-3 flex items-center gap-4 p-3 border border-ivory/10" style={{background:"#0e0e0e"}}>
          <div className="w-24 h-16 border border-ivory/10 overflow-hidden flex items-center justify-center bg-onyx">
            {f.hero_image
              ? <img data-testid="journal-cover-preview" src={f.hero_image} alt="cover" className="w-full h-full object-cover" />
              : <span className="text-[10px] tracking-widest-plus uppercase text-ivory/40">No cover</span>}
          </div>
          <div className="flex-1">
            <p className="text-[10px] tracking-widest-plus uppercase text-gold">AI cover — Nano Banana</p>
            <p className="text-xs text-ivory/60 mt-1">Generates a warm cream / beige editorial hero based on the title above. Regenerate as many times as you like.</p>
          </div>
          <button
            type="button"
            data-testid="journal-generate-cover"
            disabled={genBusy || !f.title}
            onClick={generateCover}
            className="bg-gold text-ink px-4 py-2 text-[11px] tracking-widest-plus uppercase flex items-center gap-2 disabled:opacity-40"
          >
            {genBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate cover</>}
          </button>
        </div>

        <div className="col-span-3">
          <p className="text-[10px] tracking-widest-plus uppercase text-ivory/50 mb-2">Body — rich text</p>
          <RichTextEditor value={f.body} onChange={(html) => setF({...f, body: html})} placeholder="Write the essay…" />
        </div>

        <button
          data-testid="journal-add"
          disabled={saving}
          onClick={add}
          className="col-span-3 bg-gold text-ink py-2 text-[11px] tracking-widest-plus uppercase flex items-center justify-center gap-2 disabled:opacity-40"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing…</> : <><Plus className="w-4 h-4" /> Publish essay</>}
        </button>
      </div>
      <div className="divide-y divide-ivory/10 border border-ivory/10">
        {items.map(x => (
          <div key={x.id} className="p-4 flex justify-between gap-4">
            <div className="flex-1">
              <p className="text-[10px] tracking-widest-plus uppercase text-gold">{x.category}</p>
              <p className="font-editorial text-lg">{x.title}</p>
              {x.hero_image && <img src={x.hero_image} alt="" className="mt-2 w-40 h-24 object-cover border border-ivory/10" />}
            </div>
            <button onClick={()=>del(x.id)} className="text-ivory/50 hover:text-gold"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChapterFacesEditor() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/chapters").then(r => setItems(r.data.filter(c => c.public !== false)));
  useEffect(() => { load(); }, []);
  const save = async (c) => { try { await api.patch(`/admin/chapters/${c.roman}/faces`, { male_face: c.male_face, female_face: c.female_face, hero_image: c.hero_image }); toast.success(`Chapter ${c.roman} updated`); } catch { toast.error("Failed"); } };
  const setField = (idx, k, v) => setItems(prev => prev.map((it,i) => i===idx ? {...it,[k]:v} : it));
  return (
    <div className="space-y-4">
      <p className="text-[11px] tracking-widest-plus uppercase text-gold mb-2">Chapter photography — 11 public chapters</p>
      {items.map((c, i) => (
        <div key={c.roman} className="grid grid-cols-6 gap-3 p-4 items-center border border-ivory/10" style={{background:"#141414"}}>
          <div><p className="font-editorial text-3xl">{c.roman}</p><p className="text-[10px] tracking-widest-plus uppercase text-ivory/60">{c.name}</p></div>
          <input placeholder="Male face URL" value={c.male_face || ""} onChange={e=>setField(i,"male_face",e.target.value)} className="col-span-2 bg-onyx border border-ivory/15 p-2 text-xs text-ivory" />
          <input placeholder="Female face URL" value={c.female_face || ""} onChange={e=>setField(i,"female_face",e.target.value)} className="col-span-2 bg-onyx border border-ivory/15 p-2 text-xs text-ivory" />
          <button onClick={()=>save(c)} className="bg-gold text-ink py-2 text-[11px] tracking-widest-plus uppercase">Save</button>
        </div>
      ))}
    </div>
  );
}
